// Airplane.jsx 
 
import React, { useRef, useEffect } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { Matrix4, Quaternion, Vector3 } from 'three';
import { controls, resetControls, updatePlaneAxis } from './controls';

const x = new Vector3(1, 0, 0);
const y = new Vector3(0, 1, 0);
const z = new Vector3(0, 0, 1);

export const planePosition = new Vector3(0, 3, 7);

const delayedRotMatrix = new Matrix4();
const delayedQuaternion = new Quaternion();

export function Airplane({ scale, activeTab, ...props }) {
  const { nodes, materials } = useGLTF('assets/models/aeon_dart_bc3.glb');
  const groupRef = useRef();
  const airplaneMeshRef = useRef(); // Add a ref for the airplane mesh

  useEffect(() => {
    const handleTouchStart = (e) => {
      e.preventDefault();

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;

      // Use the airplaneMeshRef to get the bounding box
      const boundingBox = airplaneMeshRef.current ? airplaneMeshRef.current.getBoundingClientRect() : null;

      if (boundingBox) {
        // Check if the touch event is within the bounding box of the airplane mesh
        if (touchX >= boundingBox.left && touchX <= boundingBox.right && touchY >= boundingBox.top && touchY <= boundingBox.bottom) {
          // Depending on the touch position, simulate key presses
          if (touchX < window.innerWidth / 2) {
            controls["a"] = true;
          } else {
            controls["d"] = true;
          }

          if (touchY < window.innerHeight / 2) {
            controls["w"] = true;
          } else {
            controls["s"] = true;
          }
        }
      }
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      resetControls(); // Use the resetControls function instead of assigning directly
    };

    // Attach touch event listeners to the airplane mesh
    const airplaneMesh = airplaneMeshRef.current;
    if (airplaneMesh) {
      airplaneMesh.addEventListener("touchstart", handleTouchStart);
      airplaneMesh.addEventListener("touchend", handleTouchEnd);
    }

    // Remove event listeners when the component is unmounted
    return () => {
      if (airplaneMesh) {
        airplaneMesh.removeEventListener("touchstart", handleTouchStart);
        airplaneMesh.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  useFrame(({ camera }) => {
    // // Ensure x, y, and z are initialized properly
    // if (!x || !y || !z) return; // NEW

    updatePlaneAxis(x, y, z, planePosition, camera);

    const rotMatrix = new Matrix4().makeBasis(x, y, z);

    // // Check if the rotation matrix is properly initialized
    // if (!rotMatrix) return; // NEW

    const matrix = new Matrix4()
      .multiply(new Matrix4().makeTranslation(planePosition.x, planePosition.y, planePosition.z))
      .multiply(rotMatrix);

    groupRef.current.matrixAutoUpdate = false;
    groupRef.current.matrix.copy(matrix);
    groupRef.current.matrixWorldNeedsUpdate = true;

    var quaternionA = new Quaternion().copy(delayedQuaternion);

    var quaternionB = new Quaternion();
    quaternionB.setFromRotationMatrix(rotMatrix);

    var interpolationFactor = 0.175;
    var interpolatedQuaternion = new Quaternion().copy(quaternionA);
    interpolatedQuaternion.slerp(quaternionB, interpolationFactor);
    delayedQuaternion.copy(interpolatedQuaternion);

    delayedRotMatrix.identity();
    delayedRotMatrix.makeRotationFromQuaternion(delayedQuaternion);

    const cameraMatrix = new Matrix4()
      .multiply(new Matrix4().makeTranslation(planePosition.x, planePosition.y, planePosition.z))
      .multiply(delayedRotMatrix)
      .multiply(new Matrix4().makeRotationX(-0.2))
      .multiply(
        new Matrix4().makeTranslation(0, 0.015, 0.3)
      );

    camera.matrixAutoUpdate = false;
    camera.matrix.copy(cameraMatrix);
    camera.matrixWorldNeedsUpdate = true;
  });

  return (
    <group ref={groupRef}>
      <group {...props} dispose={null} scale={scale} rotation-y={Math.PI}>
        <group ref={airplaneMeshRef} rotation={[-Math.PI / 2, 0, Math.PI / 2]}>
          <group rotation={[Math.PI / 2, 0, 0]}>
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Object_9.geometry}
            material={materials.Canopy}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Object_6.geometry}
            material={materials['Material.001']}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Object_7.geometry}
            material={materials.Inner_Grille}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Object_4.geometry}
            material={materials.Space_Jet_Atlas}
            position={[2.351, 1.59, 0.005]}
            rotation={[1.656, 1.473, -1.646]}
            scale={0.585}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.Object_11.geometry}
            material={materials.Boosters}
          />
          </group>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload('assets/models/aeon_dart_bc3.glb');

export default Airplane;

